/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Loader2, Search, Sparkles, MessageSquare, Clock, ChevronRight, RotateCcw,
} from 'lucide-react';
import PerceptionLadder from '@/components/terra/PerceptionLadder';
import RssScoreCard from '@/components/terra/RssScoreCard';
import EcosystemBadge from '@/components/terra/EcosystemBadge';

/**
 * Counselor Perception Tracker — the heart of Terra Growth OS Phase 1.
 * Lets a counselor (admin / health_coach / terra_head / representative-read)
 * see the lead's current perception state, the system's deterministic
 * suggestion (with rule + reason), and either accept it or move to a
 * different state with a manual reason.
 */
export default function PerceptionTrackerPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [activeLead, setActiveLead] = useState(null);
  const [states, setStates] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transitionForm, setTransitionForm] = useState({ new_state: '', reason: '', notes: '' });
  const [busy, setBusy] = useState(false);

  // Load lead list + canonical state list once.
  useEffect(() => {
    (async () => {
      try {
        const [leadsR, statesR, ecoR] = await Promise.all([
          axios.get(`${API}/inquiries`),
          axios.get(`${API}/terra-growth/perception/states`),
          axios.get(`${API}/terra-growth/ecosystems`).catch(() => ({ data: { ecosystems: [] } })),
        ]);
        const list = Array.isArray(leadsR.data) ? leadsR.data : leadsR.data?.inquiries || [];
        setLeads(list);
        setStates(statesR.data?.states || []);
        setEcosystems(ecoR.data?.ecosystems || []);
        if (list.length > 0) selectLead(list[0]);
      } catch {
        toast.error('Could not load leads.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectLead = useCallback(async (lead) => {
    setActiveLead(lead);
    setLoading(true);
    setTransitionForm({ new_state: '', reason: '', notes: '' });
    try {
      const [tR, sR] = await Promise.all([
        axios.get(`${API}/terra-growth/perception/${lead.id}/timeline`),
        axios.get(`${API}/terra-growth/perception/${lead.id}/suggest`),
      ]);
      setTimeline(tR.data);
      setSuggestion(sR.data);
    } catch {
      toast.error('Could not load perception data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const ecoMap = useMemo(() => {
    const m = {};
    ecosystems.forEach(e => { m[e.code] = e; });
    return m;
  }, [ecosystems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(l =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.city || '').toLowerCase().includes(q),
    );
  }, [search, leads]);

  const acceptSuggestion = async () => {
    if (!suggestion?.primary || !activeLead) return;
    setBusy(true);
    try {
      await axios.post(`${API}/terra-growth/perception/${activeLead.id}/transition`, {
        new_state: suggestion.primary.state,
        reason: suggestion.primary.reason,
        rule_id: suggestion.primary.rule_id,
      });
      toast.success(`Moved to ${suggestion.primary.state}.`);
      await selectLead(activeLead);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transition failed.');
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    if (!transitionForm.new_state || !activeLead) {
      toast.error('Pick a target state first.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/terra-growth/perception/${activeLead.id}/transition`, {
        new_state: transitionForm.new_state,
        reason: transitionForm.reason || null,
        notes: transitionForm.notes || null,
      });
      toast.success(`Moved to ${transitionForm.new_state}.`);
      setTransitionForm({ new_state: '', reason: '', notes: '' });
      await selectLead(activeLead);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transition failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="perception-tracker-page" className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239] font-['Work_Sans']">
            Terra Growth OS™ · Layer 3
          </p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">
            Perception Tracker
          </h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
            Move a lead through the 12-state perception ladder. The system suggests the next state from
            deterministic rules — accept it, modify it, or override with a manual reason.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ── Lead list ── */}
          <aside className="lg:col-span-3 rounded-2xl border border-[#EBE8E3] bg-white p-3">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-3 h-3 text-[#8A8681]" />
              <Input
                data-testid="perception-search"
                placeholder="Search leads…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <ul className="space-y-1 max-h-[70vh] overflow-y-auto">
              {filtered.map(l => {
                const eco = ecoMap[l.ecosystem_code];
                const isActive = activeLead?.id === l.id;
                return (
                  <li key={l.id}>
                    <button
                      data-testid={`perception-lead-${l.id}`}
                      onClick={() => selectLead(l)}
                      className={`w-full text-left rounded-xl border px-3 py-2 transition-all ${isActive ? 'border-[#4A5D4E] bg-[#F4F2EE]' : 'border-transparent hover:bg-[#FBFaf9]'}`}
                    >
                      <div className="text-sm text-[#2C2A29] font-['Work_Sans'] truncate">{l.name || '—'}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {l.perception_state && (
                          <span className="text-[10px] uppercase tracking-[0.14em] text-[#5C5955] bg-[#F4F2EE] rounded-full px-2 py-0.5">
                            {l.perception_state}
                          </span>
                        )}
                        {eco && <EcosystemBadge ecosystem={eco} compact />}
                      </div>
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="text-xs text-[#8A8681] py-4 text-center">No leads match.</li>
              )}
            </ul>
          </aside>

          {/* ── Detail ── */}
          <main className="lg:col-span-9 space-y-6">
            {!activeLead ? (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
                Select a lead to begin.
              </div>
            ) : loading ? (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
                <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
              </div>
            ) : (
              <>
                {/* Lead header */}
                <div data-testid="perception-lead-header" className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                  <div className="flex items-baseline justify-between flex-wrap gap-3">
                    <div>
                      <h2 className="text-xl font-['Outfit'] font-light text-[#2C2A29]">{activeLead.name}</h2>
                      <p className="text-xs text-[#8A8681] mt-0.5">
                        {activeLead.email || 'no email'} · {activeLead.phone || 'no phone'} · {activeLead.city || '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {activeLead.pipeline_stage && (
                        <span data-testid="lead-crm-stage"
                              className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[#F4F2EE] text-[#5C5955] border border-[#D8D4CE]">
                          CRM · {activeLead.pipeline_stage}
                        </span>
                      )}
                      {ecoMap[activeLead.ecosystem_code] && (
                        <EcosystemBadge ecosystem={ecoMap[activeLead.ecosystem_code]} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Suggestion card */}
                {suggestion?.primary && (
                  <div data-testid="perception-suggestion-card"
                       className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1] p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#FAEAD0] flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-3.5 h-3.5 text-[#8C6239]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239] font-['Work_Sans']">
                          Suggested by system · rule: {suggestion.primary.rule_id}
                        </p>
                        <p className="text-base font-['Outfit'] text-[#5C3D1F] mt-1">
                          Move to <span className="font-medium" data-testid="perception-suggestion-state">{suggestion.primary.state}</span>
                        </p>
                        <p className="text-sm text-[#7A5A3F] font-['Work_Sans'] mt-1">
                          {suggestion.primary.reason}
                        </p>
                      </div>
                      <Button
                        data-testid="perception-accept-suggestion-btn"
                        onClick={acceptSuggestion} disabled={busy || timeline?.current_state === suggestion.primary.state}
                        className="bg-[#8C6239] hover:bg-[#75502F] text-white text-xs rounded-full px-4"
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                        Accept
                      </Button>
                    </div>
                    {(suggestion.alternatives || []).length > 0 && (
                      <div className="mt-3 ml-11 flex flex-wrap gap-1.5">
                        <span className="text-[10px] uppercase tracking-[0.16em] text-[#8C6239]">Alternatives:</span>
                        {suggestion.alternatives.map(alt => (
                          <span key={alt.rule_id}
                                className="text-[10px] uppercase tracking-[0.14em] bg-white border border-[#E5D6BE] rounded-full px-2 py-0.5 text-[#7A5A3F]">
                            {alt.state}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Ladder + manual transition */}
                  <div className="space-y-4">
                    <PerceptionLadder
                      states={states}
                      currentCode={timeline?.current_state}
                      suggestionCode={suggestion?.primary?.state}
                      onSelect={(code) => setTransitionForm(f => ({ ...f, new_state: code }))}
                    />
                    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
                        Manual transition
                      </p>
                      <div className="space-y-2">
                        <Input
                          data-testid="perception-manual-state-input"
                          placeholder="Target state (click ladder rung above)"
                          value={transitionForm.new_state} readOnly
                          className="h-9 text-sm"
                        />
                        <Input
                          data-testid="perception-manual-reason-input"
                          placeholder="Reason (optional but recommended)"
                          value={transitionForm.reason}
                          onChange={(e) => setTransitionForm(f => ({ ...f, reason: e.target.value }))}
                          className="h-9 text-sm"
                        />
                        <Input
                          data-testid="perception-manual-notes-input"
                          placeholder="Notes — discussion / event context"
                          value={transitionForm.notes}
                          onChange={(e) => setTransitionForm(f => ({ ...f, notes: e.target.value }))}
                          className="h-9 text-sm"
                        />
                        <Button
                          data-testid="perception-manual-submit-btn"
                          onClick={submitManual} disabled={busy || !transitionForm.new_state}
                          className="bg-[#4A5D4E] hover:bg-[#3D4F40] text-white text-xs rounded-full px-4 mt-1"
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                          Move
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Timeline + RSS */}
                  <div className="space-y-4">
                    <div data-testid="perception-timeline" className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3 flex items-center gap-2">
                        <Clock className="w-3 h-3" /> Timeline
                      </p>
                      {(!timeline || (timeline.history || []).length === 0) ? (
                        <p className="text-xs text-[#8A8681]">No transitions yet.</p>
                      ) : (
                        <ol className="space-y-3">
                          {timeline.history.map(h => (
                            <li key={h.id} data-testid={`perception-timeline-row-${h.id}`}
                                className="border-l-2 border-[#D8D4CE] pl-3">
                              <div className="text-xs text-[#2C2A29] font-['Work_Sans']">
                                <span className="text-[#8A8681]">{h.from_state}</span>
                                <ChevronRight className="w-3 h-3 inline mx-1 text-[#B5B0A8]" />
                                <span className="font-medium">{h.new_state}</span>
                                {h.source === 'system_suggestion' && (
                                  <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-[#8C6239] bg-[#FAF1E1] rounded-full px-1.5 py-0.5">
                                    rule
                                  </span>
                                )}
                              </div>
                              {h.reason && <div className="text-[11px] text-[#5C5955] mt-0.5">{h.reason}</div>}
                              {h.notes  && <div className="text-[11px] text-[#8A8681] mt-0.5 italic">{h.notes}</div>}
                              <div className="text-[10px] text-[#B5B0A8] mt-0.5 flex items-center gap-2">
                                <span>{new Date(h.transitioned_at).toLocaleString()}</span>
                                {h.transitioned_by_role && (
                                  <span className="text-[#5C5955] uppercase tracking-[0.12em]">
                                    {h.transitioned_by_role.replace('_', ' ')}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>

                    <RssScoreCard leadId={activeLead.id} isAdmin={isAdmin} role={user?.role} />
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
}
